import React, { useState } from 'react'
import * as yup from 'yup'
import { useTrans, useTealium } from '@next-nx/hooks'
import styles from './NewsletterBox.module.sass'
import { Button, FormCheckbox, FormField, FormInput, WContainer } from '@next-nx/shared-ui/atoms'
import { useCreateUtenteNewsletterMutation } from '@next-nx/shared-graphql/unipolrental'
import { Form, Formik } from 'formik'
import { getTimestamp } from '@next-nx/utils';

interface Props {
  className?: string
  onSubmit: (status: string | undefined) => void
}

const NewsletterBox = (props: Props) => {
  const { className = '', onSubmit = () => {} } = props

  const t = useTrans()
  const { linkDataLayer } = useTealium()

  const [validateOnChange, setValidateOnChange] = useState(false)
  const [createUtenteNewsletterMutation] = useCreateUtenteNewsletterMutation()

  const validationSchema = yup.object().shape({
    email: yup.string().email(t('Email non valida')).required(t('Campo obbligatorio')),
    privacy: yup.bool().oneOf([true], t('Campo obbligatorio')),
  })

  const initialValues = {
    email: '',
    privacy: false,
  }

  const handleTracking = () => {
    const action_timestamp = getTimestamp() 
    
    linkDataLayer({
          virtual: 'action', 
          action_name: 'newsletter',  
          action_tech: 'click',
          action_type: 'iscrizione_ok',
          action_timestamp, 
          action_effect: 'iscrizione_newsletter',
        })
  } 

  return (
    <div className={`${styles.root} ${className}`}>
      <div className={styles.bg} />
      <WContainer>
        <div className={styles.layout}>
          <div className={styles.content}>
            <p className={styles.title}>{t('Iscriviti alla nostra Newsletter')}</p>
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: t('newsletter_box_description') }}
            />
          </div>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            validateOnChange={validateOnChange}
            validateOnBlur={validateOnChange}
            onSubmit={(values, actions) => {

              createUtenteNewsletterMutation({
                variables: {
                  input: {
                    email: values.email
                  }
                }
              })
              ?.then((res) => {
                const status = res?.data?.createUtenteNewsletter?.status
                const message = res?.data?.createUtenteNewsletter?.message
                if (status) {
                  handleTracking()
                  onSubmit('success')
                  actions.resetForm()
                } else {
                  onSubmit('error')
                }
              })
              .catch((err) => {
                console.log(err)
                onSubmit('error')
              })
              actions.setSubmitting(false)
            }}
          >
            {({ values, errors, touched, isValid, isSubmitting, handleBlur, handleChange, ...props }) => (      
              <Form className={styles.form}>
                <FormField label={t('E-mail')} color="white" layout="full">
                  <FormInput
                    type="email"
                    placeholder={t('Inserisci la tua e-mail')}
                    value={values.email}
                    onChange={handleChange('email')}
                    onBlur={handleBlur('email')}
                    status={errors?.email ? 'error' : 'success'}
                    errorMessage={errors?.email}
                    variant='dark-bg'
                  />
                </FormField>
                <FormField layout="full">
                  <FormCheckbox
                    id="newsletter_box_privacy"
                    label={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: t(
                            "Ho preso visione dell’{0}informativa privacy{1} e autorizzo all’invio di comunicazioni.",
                            ['<a href="/privacy-policy" target="_blank">', '</a>']
                          ),
                        }}
                      />
                    }
                    size="sm"
                    color="white"
                    checked={values.privacy}
                    onChange={handleChange('privacy')}
                    onBlur={handleBlur('privacy')}
                    value={1}
                    status={errors?.privacy ? 'error' : 'success'}
                    errorMessage={errors?.privacy}
                    variant='dark-bg'
                  />
                </FormField>
                <div className={styles.submitBox}>
                  <Button
                    type="submit"
                    label={t('Iscriviti alla Newsletter')}
                    disabled={isSubmitting}
                    onClick={() => setValidateOnChange(true)}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </WContainer>
    </div>
  )
}

export default NewsletterBox
